import React, { useState, useCallback, useEffect } from 'react';
import { ReactSortable } from "react-sortablejs";
import Cropper from 'react-easy-crop';

// Componente de Select para categorías
const SelectCategoria = ({ value, onChange, categorias = [] }) => {
    return (
        <select 
            className="form-select" 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white' }}
        >
            <option value="">Seleccionar categoría</option>
            {categorias.map((categoria, index) => (
                <option key={index} value={categoria.value || categoria.id || categoria}>
                    {categoria.label || categoria.nombre || categoria}
                </option>
            ))}
        </select>
    );
};
function InputImagenesTitulo({multiImagenes,handleChangeImagen,maxImages,ratio,name,errores,categorias=[],sucursal}) {
    const MAX_IMAGES = maxImages || 5;
    const [images, setImages] = useState(multiImagenes);
    const [error, setError] = useState('');

    useEffect(() => {
        handleChangeImagen(images);
    }, [images]);
    
    // Estado para el modal de recorte
    const [modalOpen, setModalOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [aspectRatio, setAspectRatio] = useState(ratio || 4 / 3);
    
    // Estados para los campos adicionales del modal
    const [imagenTitulo, setImagenTitulo] = useState('');
    const [imagenCategoria, setImagenCategoria] = useState('');

    // Función para manejar el completado del recorte
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Función para crear una imagen a partir de una URL
    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.src = url;
        });

    // Función para detectar si una imagen tiene transparencia
    const hasTransparency = (imageData) => {
        const data = imageData.data;
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) return true; // Si algún píxel tiene canal alpha < 255
        }
        return false;
    };

    // Función para obtener la imagen recortada
    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        // Dibujar la imagen en el canvas
        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        // Verificar si la imagen tiene transparencia
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const transparent = hasTransparency(imageData);
        const mimeType = transparent ? 'image/png' : 'image/jpeg';
        const quality = 0.92; // Calidad para JPEG

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => resolve(blob),
                mimeType,
                quality
            );
        });
    };


    // Función para completar el recorte y guardar la imagen
    const handleCropSave = useCallback(async () => {
        try {
            if (!currentImage || !croppedAreaPixels) return;
            
            const croppedBlob = await getCroppedImg(currentImage.preview, croppedAreaPixels);
            // Mantener la extensión original o usar .png para transparencia
            const fileExt = currentImage.file.name.split('.').pop().toLowerCase();
            const isTransparent = fileExt === 'png' || fileExt === 'gif' || fileExt === 'webp';
            const mimeType = isTransparent ? 'image/png' : 'image/jpeg';
            const newFileName = currentImage.file.name.replace(/\.[^/.]+$/, '') + (isTransparent ? '.png' : '.jpg');
            
            const croppedFile = new File([croppedBlob], newFileName, { type: mimeType });
            const croppedPreview = URL.createObjectURL(croppedBlob);
            
            // Si la imagen ya existe en el array, actualízala
            if (images.some(img => img.id === currentImage.id)) {
                setImages(prevImages => prevImages.map((img) => 
                    img.id === currentImage.id  ? { 
                        ...img, 
                        file: croppedFile, 
                        preview: croppedPreview,
                        titulo: imagenTitulo,
                        categoria: imagenCategoria
                    }  : img
                ));
            } else {
                // Si es una imagen nueva, agrégala al array
                const base64 = await toBase64(croppedFile);
                const newImage = {
                    id: currentImage.id,
                    file: croppedFile,
                    preview: base64,
                    esNuevo: true,
                    nombre: currentImage.file.name,
                    titulo: imagenTitulo,
                    categoria: imagenCategoria,
                    sucursal
                };
                setImages(prevImages => [...prevImages, newImage]);
            }
            
            // Cerrar el modal y resetear los campos
            setModalOpen(false);
            setCurrentImage(null);
            setImagenTitulo('');
            setImagenCategoria('');
        } catch (error) {
            console.error('Error al recortar la imagen:', error);
        }
    }, [currentImage, croppedAreaPixels, imagenTitulo, imagenCategoria]);

    // Función para cerrar el modal sin guardar
    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentImage(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setImagenTitulo('');
        setImagenCategoria('');
    };

    // Función para convertir un archivo a base64
    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

    // Función para manejar la carga de imágenes
    const handleImageUpload = async (event) => {
        // Reset previous error
        setError('');

        const files = Array.from(event.target.files);
        
        // Check if adding new images would exceed the limit
        if (images.length + files.length > MAX_IMAGES) {
            setError(`Solo puedes subir un máximo de ${MAX_IMAGES} imágenes`);
            return;
        }

        try {
            // Si es una sola imagen, abre el modal de recorte
            if (files.length === 1) {
                const file = files[0];
                
                const newImage = {
                    id: `${Date.now()}-${file.name}`,
                    file: file,
                    preview: URL.createObjectURL(file),
                };
                setCurrentImage(newImage);
                setModalOpen(true);
            } else {
                // Si son múltiples imágenes, agrégalas directamente
                const newImages = await Promise.all(files.map(async (file) => {
                    return {
                        id: `${Date.now()}-${file.name}`, 
                        file: file,
                        preview: URL.createObjectURL(file),
                    };
                }));

                setImages(prevImages => [...prevImages, ...newImages]);
            }
        } catch (error) {
            console.error('Error al convertir la imagen a base64:', error);
            setError('Error al procesar las imágenes');
        }
        
        // Limpiar el input de archivo
        event.target.value = '';
    };

    const handleRemoveImage = (id) => {
        setImages(prevImages => prevImages.filter(image => image.id !== id));
        // Clear any previous error when removing an image
        setError('');
    };

    // Limpiar URLs de objetos al desmontar el componente
    React.useEffect(() => {
        return () => {
            images.forEach(image => {
                URL.revokeObjectURL(image.preview);
            });
        };
    }, [images]);

    return (
        <div className="image-upload-container">
            <label
                className="form-label fw-semibold text-primary-light text-sm mb-8 d-flex justify-content-start"
            >
                Imagen <span className="text-danger-600"></span> <span className="text-primary-light ms-2">{images.length}/{MAX_IMAGES}</span>
            </label>
            {images.length < MAX_IMAGES && (
                <div className="input-group">
                    <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload} 
                        title='Subir Imagen'
                        className={`form-control mb-3 ${errores?.[name] ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">
                        {errores?.[name]}
                    </div>
                </div>
            )}

            {error && (
                <div className="error-message">{error}</div>
            )}

            {images.length > 0 && (
                <ReactSortable 
                    list={images} 
                    setList={setImages} 
                    className="image-grid"
                >
                    {images.map((image, index) => (
                        <div key={image.id} className="image-item">
                            <img 
                                src={image.preview} 
                                // alt={image.file.name} 
                                className="image-preview"
                            />
                            <p>Posicion: {index + 1}</p>
                            <button 
                                onClick={() => handleRemoveImage(image.id)}
                                className="remove-button"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </ReactSortable>
            )}

            {images.length === MAX_IMAGES && (
                <p className="max-images-message">Límite máximo de imágenes alcanzado</p>
            )}

            {/* Modal para recortar imágenes */}
            {modalOpen && currentImage && (
                <div className="crop-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div className="crop-modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={{ color: '#333', marginTop: '0', marginBottom: '20px', textAlign: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Recortar Imagen</h2>
                        <div className="crop-container" style={{ position: 'relative', height: '300px', width: '100%', backgroundColor: '#f0f0f0', marginBottom: '20px' }}>
                            <Cropper
                                image={currentImage.preview}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspectRatio}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="crop-controls" style={{ marginBottom: '20px' }}>
                            <div className="zoom-control" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{color: '#333', fontWeight: 'bold', minWidth: '50px'}}>Zoom:</label>
                                <input 
                                    type="range" 
                                    value={zoom} 
                                    min={1} 
                                    max={3} 
                                    step={0.1} 
                                    onChange={(e) => setZoom(Number(e.target.value))} 
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                        
                        {/* Campos adicionales para la información de la imagen */}
                        <div className="image-info-fields" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                            <h3 style={{ marginTop: '0', marginBottom: '15px', color: '#333', fontSize: '16px' }}>Información de la imagen</h3>
                            <div className="form-group mb-3">
                                <label htmlFor="imagenTitulo" style={{color: '#333', fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Título de la imagen:</label>
                                <input 
                                    type="text" 
                                    id="imagenTitulo"
                                    className="form-control" 
                                    value={imagenTitulo}
                                    onChange={(e) => setImagenTitulo(e.target.value)}
                                    placeholder="Ingrese un título para la imagen"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                            
                            <div className="form-group mb-3">
                                <label htmlFor="imagenCategoria" style={{color: '#333', fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Categoría:</label>
                                <SelectCategoria 
                                    value={imagenCategoria}
                                    onChange={setImagenCategoria}
                                    categorias={categorias}
                                />
                            </div>
                        </div>
                        
                        <div className="crop-modal-buttons" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '10px' }}>
                            <button 
                                onClick={handleCloseModal} 
                                className="cancel-button"
                                style={{ padding: '8px 16px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleCropSave} 
                                className="save-button"
                                style={{ padding: '8px 16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InputImagenesTitulo;
